<template>
    <div>
        <div class="mb-3">
            <Link as="button"
                  :href="$route('order.details', [store.id, order.url_key])"
                  :only="['users']"
                  class="btn bg-white text-dark btn-hover-primary">
                <i class="fas fa-chevron-circle-left text-primary"/>
                Zurück zur Bestellung
            </Link>
        </div>

        <div class="card card-body bg-danger text-white">
            <div class="d-flex flex-row gap-3">
                <div class="flex-grow-1">
                    <order-info-box :order="order" :store="store"/>
                </div>
                <div v-if="order.type === 'delivery'" class="text-right text-lg">
                    <h3>{{ __('general.address.shipping') }}</h3>
                    <div v-if="order.mail_address_is_shipping_address">{{ __('general.like') }} {{
                            __('general.address.invoice')
                        }}
                    </div>
                    <order-address v-else :address="shippingAddress"/>
                </div>
                <div v-if="receiptAddress !== undefined" class="text-right text-lg">
                    <h3>{{ __('general.address.invoice') }}</h3>
                    <order-address :address="receiptAddress"/>
                </div>
            </div>
        </div>

        <div class="card mt-3">
            <div class="card-header">
                <div class="card-title m-0">
                    <h3 class="card-label m-0">
                        <i class="fa fa-hand-holding-usd mr-1"/>
                        {{ __('actions.refund') }}
                    </h3>
                </div>
            </div>
            <div class="card-body">
                <div class="form-group">
                    <label class="font-weight-bold">
                        {{ __('orders.details.refund.dropdown.title') }}
                    </label>
                    <select class="form-control w-xl-50" v-model="reason" :class="{'border-danger': reason === ''}">
                        <option value="" disabled>
                            {{ __('actions.please_select') }}
                        </option>
                        <option v-for="(refundOptionReadable, refundOptionType) in refundOptions"
                                :value="refundOptionType"
                                v-text="refundOptionReadable"/>
                    </select>
                    <span class="form-text text-muted">
                        {{ __('orders.details.refund.dropdown.choose_type') }}
                    </span>
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">
                        {{ __('orders.details.refund.choose_orders') }}
                    </label>
                    <order-items class="border rounded p-4"
                                 :order="order"
                                 :show-total="false"
                                 :refund-mode="true"
                                 :empty-refund="isEmptyRefund"
                                 v-model="refundQuantities"
                    />
                    <span class="form-text text-muted">
                        {{ __('orders.details.refund.refund_info') }}
                    </span>
                </div>
            </div>

            <div class="card-footer">
                <div class="d-flex gap-3">
                    <div class="flex-grow-1">
                        <Link as="button"
                              :href="$route('order.details', [store.id, order.url_key])" :only="['order', 'users']"
                              class="btn btn-secondary">
                            <i class="fas fa-reply"/>
                            {{ __('actions.abort') }}
                        </Link>
                    </div>
                    <div v-if="isEmptyRefund" class="d-flex flex-column justify-content-center">
                        <div class="text-right text-danger">
                            <i class="fas fa-exclamation-triangle mr-1"/>
                            {{ __('orders.details.refund.no_articles_selected') }}
                        </div>
                    </div>
                    <div v-if=loading class="d-flex justify-center items-center">
                        <div class="spinner spinner-danger mr-10"></div>
                    </div>
                    <div v-else>
                        <button class="btn btn-danger"
                                :class="{'d-none d-md-inline-block': isEmptyRefund}"
                                @click="refundOrder"
                                :disabled="isEmptyRefund || wasRefunded"
                        >
                            <i class="fas fa-hand-holding-usd"/>
                            <span v-if="isPartialRefund && !isEmptyRefund">{{ __('actions.partial_refund') }}</span>
                            <span v-else>{{ __('actions.refund') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import OrderInfoBox from '@components/Orders/OrderInfoBox';
import OrderAddress from '@components/Orders/OrderAddress';
import OrderItems from '@components/Orders/Tabs/OrderItems.vue';
import OrderBadges from '@components/Orders/OrderBadges';
import OrderStateInfo from '@components/Orders/OrderStateInfo';
import BreadcrumbsWrapper from '@components/Breadcrumbs/BreadcrumbsWrapper';
import Breadcrumb from '@components/Breadcrumbs/Breadcrumb';
import { Link } from '@inertiajs/vue2';
import StoreLayout from '@layouts/StoreLayout.vue';
import {OrderState} from '@composables/orderStates';

export default {
    layout: StoreLayout,

    components: {
        OrderStateInfo,
        OrderInfoBox,
        OrderAddress,
        OrderItems,
        OrderBadges,
        BreadcrumbsWrapper,
        Breadcrumb,
        Link,
    },

    props: {
        refundOptions: Object,
        order: Object,
        store: Object,
    },

    data() {
        return {
            reason: '',
            refundQuantities: {},
            loading: false,
            wasRefunded: false,
        };
    },

    beforeMount() {
        for (let item of this.order.items) {
            this.refundQuantities[item.identifier] = item.quantity;
        }
    },

    methods: {
        refundOrder() {
            this.loading = true;

            this.$inertia.patch(this.$route('order.refund', [this.store.id, this.order.url_key]), {
                reason: this.reason,
                quantities: this.refundQuantities,
            }, {
                onSuccess: () => {
                    this.loading = false;
                    this.wasRefunded = true;
                },
                onError: (errors) => {
                    toastr.error(errors.reason);
                    this.loading = false;
                },
            });
        },
    },

    computed: {
        shippingAddress() {
            for (let address of this.order.addresses) {
                if (address.pivot.type === 'ship_to') {
                    return address;
                }
            }
        },

        receiptAddress() {
            for (let address of this.order.addresses) {
                if (address.pivot.type === 'mail_to') {
                    return address;
                }
            }
        },

        isFulfilled() {
            return this.order.state === OrderState.PickedUp || this.order.state === OrderState.Send;
        },

        isRefunded() {
            return this.order.state === OrderState.Refunded;
        },

        isPending() {
            return this.order.state === OrderState.Pending;
        },

        isReadyForPickup() {
            return this.order.state === OrderState.ReadyForPickup && this.order.type === 'pickup';
        },

        isConfirmedForPickup() {
            return this.order.state === OrderState.Confirmed && this.order.type === 'pickup';
        },

        isConfirmedForDelivery() {
            return this.order.state === OrderState.Confirmed && this.order.type === 'delivery';
        },

        isPartialRefund() {
            for (let item of this.order.items) {
                if (!this.refundQuantities.hasOwnProperty(item.identifier)) {
                    return true;
                }

                if (this.refundQuantities[item.identifier] !== item.quantity) {
                    return true;
                }
            }

            return false;
        },

        isEmptyRefund() {
            for (let item of this.order.items) {
                if (!this.refundQuantities.hasOwnProperty(item.identifier)) {
                    return true;
                }

                if (this.refundQuantities[item.identifier] > 0) {
                    return false;
                }
            }

            return true;
        },
    },
};
</script>
